import { NotificationBannerData } from '@/app/models/notification-banner.model';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { LinkButtonComponent } from '../../shared/components/link-button/link-button.component';
import { ExternalUrlsService } from '@/app/services/external-urls.service';
import { DispatchService } from '@/app/services/dispatch.service';
import { DispatchViewModel, DispatchStatus } from '@/app/models/dispatch.model';
import moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';
import { FormatDateTimePipe } from '@/app/pipes/format-date-time.pipe';
import { CookieService } from 'ngx-shared-services';
import { MixPanelEvent } from '@/app/services/mixpanel.service';
@Component({
  selector: 'dr-flex-home-ui-notification-banner',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [NgClass, LinkButtonComponent, TranslateModule],
  providers: [FormatDateTimePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './notification-banner.component.html',
  styleUrl: './notification-banner.component.scss',
})
export class NotificationBannerComponent implements OnInit {
  notificationData = signal<NotificationBannerData | undefined>(undefined);

  private readonly dispatchService = inject(DispatchService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly externalUrlsService = inject(ExternalUrlsService);
  private readonly translate = inject(TranslateService);
  readonly mixPanelEvent = MixPanelEvent;

  readonly activeEventsUrl = this.externalUrlsService.activeEvents;

  ngOnInit() {
    this.translate.onLangChange
      .pipe(
        switchMap(() => this.dispatchService.getDispatchesRows()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((activeDispatches) => this.setNotification(activeDispatches));
  }
  private getNotificationType(status: DispatchStatus): 'alert' | 'error' | 'success' {
    switch (status) {
      case DispatchStatus.Upcoming:
        return 'alert';
      case DispatchStatus.None:
        return 'success';
      case DispatchStatus.Ongoing:
        return 'error';
      case DispatchStatus.RecentlyEnded:
        return 'alert';
    }
  }
  private setNotification(activeDispatches: DispatchViewModel[]): void {
    //const locale = this.cookieService.getI18NLocale() || environment.defaults.locale;
    const translationPrefix = 'FLEX_HOME.NOTIFICATION_BANNER';
    if (activeDispatches.length > 0 && activeDispatches[0].status !== DispatchStatus.None) {
      const dispatch =
        activeDispatches.find((dispatch) => dispatch.status === DispatchStatus.Ongoing) ||
        activeDispatches.find((d) => d.status === DispatchStatus.RecentlyEnded) ||
        activeDispatches[0];
      const start = moment(dispatch.startUtc);
      const now = moment();
      const diffMinutes = start.diff(now, 'minutes');
      const dispatchStartFormatted = moment.tz(dispatch.startUtc, dispatch.timeZone).format('hh:mm A z');
      const dispatchEndFormatted = dispatch.endUtc ? moment.tz(dispatch.endUtc, dispatch.timeZone).format('hh:mm A z') : null;

      const messageBase = this.translate.instant(`${translationPrefix}.MESSAGE.REDUCE_NOW`, {});

      let message = dispatchEndFormatted ? `${messageBase} ${this.translate.instant(`${translationPrefix}.MESSAGE.UNTIL`, { end: dispatchEndFormatted })}` : messageBase;
      if (dispatch.status === DispatchStatus.Ongoing) {
        this.notificationData.set({
          notificationType: this.getNotificationType(dispatch.status),
          title: this.translate.instant(`${translationPrefix}.TITLE.DISPATCH_AT`, { site: dispatch.site }),
          message,
        });
      } else if (dispatch.status === DispatchStatus.RecentlyEnded) {
        message = this.translate.instant(`${translationPrefix}.MESSAGE.ENDED_RECENTLY`, { end: dispatchEndFormatted });
        this.notificationData.set({
          notificationType: this.getNotificationType(dispatch.status),
          title: this.translate.instant(`${translationPrefix}.TITLE.DISPATCH_ENDED`, { site: dispatch.site }),
          message,
        });
      } else if (dispatch.status === DispatchStatus.Upcoming) {
        let differenceMessage: string;
        const andLabel = this.translate.instant(`${translationPrefix}.DURATION.AND`);

        const safeDiffMinutes = Math.ceil(Math.max(diffMinutes, 1));
        if (safeDiffMinutes >= 1440) {
          // More than 24 hours
          const days = Math.floor(safeDiffMinutes / 1440);
          const hours = Math.floor((safeDiffMinutes % 1440) / 60);

          const daysLabel = this.translate.instant(days > 1 ? `${translationPrefix}.DURATION.DAYS` : `${translationPrefix}.DURATION.DAY`);

          if (hours !== 0) {
            const hoursLabel = this.translate.instant(hours !== 1 ? `${translationPrefix}.DURATION.HOURS` : `${translationPrefix}.DURATION.HOUR`);
            differenceMessage = `${days} ${daysLabel} ${andLabel} ${hours} ${hoursLabel}`;
          } else {
            differenceMessage = `${days} ${daysLabel}`;
          }
        } else if (safeDiffMinutes >= 120) {
          // Between 2 and 24 hours
          const hours = Math.floor(safeDiffMinutes / 60);
          const hoursLabel = this.translate.instant(hours > 1 ? `${translationPrefix}.DURATION.HOURS` : `${translationPrefix}.DURATION.HOUR`);
          differenceMessage = `${hours} ${hoursLabel}`;
        } else if (safeDiffMinutes >= 60) {
          // Between 1 and 2 hours
          const hours = Math.floor(safeDiffMinutes / 60);
          const minutes = safeDiffMinutes % 60;
          const hoursLabel = this.translate.instant(hours > 1 ? `${translationPrefix}.DURATION.HOURS` : `${translationPrefix}.DURATION.HOUR`);
          const minutesLabel = this.translate.instant(minutes !== 1 ? `${translationPrefix}.DURATION.MINUTES` : `${translationPrefix}.DURATION.MINUTE`);

          differenceMessage = `${hours} ${hoursLabel}${minutes !== 0 ? ` ${andLabel} ${minutes} ${minutesLabel}` : ''}`;
        } else {
          // Less than 60 minutes
          const minutesLabel = this.translate.instant(safeDiffMinutes !== 1 ? `${translationPrefix}.DURATION.MINUTES` : `${translationPrefix}.DURATION.MINUTE`);
          differenceMessage = `${safeDiffMinutes} ${minutesLabel}`;
        }
        const basemsg = this.translate.instant(`${translationPrefix}.MESSAGE.REDUCE_IN_FROM_TO`, {
          difference: differenceMessage,
          start: dispatchStartFormatted,
        });
        message = dispatchEndFormatted ? `${basemsg} ${this.translate.instant(`${translationPrefix}.MESSAGE.NO_NULL_END_DATE`, { end: dispatchEndFormatted })}` : basemsg;
      }
      //console.log(message);
      //console.log(this.translate.instant(`${translationPrefix}.TITLE.DISPATCH_AT`, { site: dispatch.site }));

      this.notificationData.set({
        notificationType: this.getNotificationType(dispatch.status),
        title: this.translate.instant(`${translationPrefix}.TITLE.DISPATCH_AT`, { site: dispatch.site }),
        message,
      });
    } else {
      this.notificationData.set({
        notificationType: 'success',
        title: this.translate.instant(`${translationPrefix}.TITLE.DEFAULT`),
        message: this.translate.instant(`${translationPrefix}.MESSAGE.NO_CRITICAL_ACTION`),
      });
    }
  }
}
